import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Heading, ModalControlModalProps, NakedButton, Tray, useAlert, useForm } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useCheckPaymentsEnabled } from '../../hooks/use-check-payments-enabled';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { ServiceTypesSettingsTrackingIds } from '../../trackingIds/service-types-settings-tracking';
import { ServiceTypeForm } from './form';

export type ServiceTypeVariant = 'core' | 'integrated' | 'general';

type Props = {
  locationId?: string;
  selectedServiceDetails: SchedulerV3.Types.SelectedServiceType | null;
  modalProps: ModalControlModalProps;
  variant: ServiceTypeVariant;
  onClose: () => void;
};

// TODO: Rename file
export const AddEditServiceTypeTray = ({ locationId, selectedServiceDetails, modalProps, variant, onClose }: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule-settings');
  const { isMultiLocation, getScopeName } = useSchedulingLocationInfo();
  const { parentLocationId } = useSchedulingLocationInfo();
  const { isPaymentsEnabled } = useCheckPaymentsEnabled(locationId);

  const selectedAppointmentType = selectedServiceDetails?.appointmentTypeGroup.appointmentType;
  const selectedServiceType = selectedServiceDetails?.bookableAppointmentType;
  const isEditMode = !!selectedServiceType;
  const apptTypeName = selectedAppointmentType?.displayName ?? selectedAppointmentType?.externalName ?? '';
  // NOTE: For core locations, sourceTenantId is the locationId. This same logic is also applied for Appointment Type creation.
  const sourceTenantId = variant === 'core' ? locationId : selectedAppointmentType?.sourceTenantId;
  const showLocationChip = isMultiLocation && locationId;

  const title = useMemo(() => {
    const variantTextMap = {
      core: t('Service Type'),
      general: t('Service Type for General Request'),
      integrated: t('Service Type for {{apptTypeName}}', { apptTypeName }),
    };
    const serviceTypeText = variantTextMap[variant] || '';
    const actionText = isEditMode ? t('Edit') : t('Add');

    return `${actionText} ${serviceTypeText}`;
  }, [apptTypeName, isEditMode, t, variant]);

  const { isComplete, values, getFieldProps, reset } = useForm({
    fields: {
      color: {
        type: 'text',
        value: selectedAppointmentType?.details?.clr ?? PRIMARY_EVENT_COLOR,
        required: true,
      },
      appointmentType: {
        type: 'dropdown',
        value: selectedAppointmentType?.id ?? selectedServiceType?.appointmentTypeId ?? '',
        hidden: variant !== 'integrated',
        required: true,
      },
      source: {
        type: 'dropdown',
        value: selectedAppointmentType?.sourceId ?? '',
        hidden: variant === 'core',
        required: true,
      },
      serviceName: {
        type: 'text',
        value: selectedServiceType?.displayName ?? '',
        required: true,
      },
      duration: {
        type: 'number',
        value: String(isEditMode ? selectedServiceType?.duration : selectedAppointmentType?.details?.dur) ?? '',
        required: true,
        min: 5,
        max: 1440,
        allowDecimal: false,
        allowNegative: false,
      },
      procedureCodes: {
        type: 'multiselect',
        value: [],
        hidden: true, // TODO: Enable for integrated and general variants, when procedure code API is ready
      },
      serviceDescription: {
        type: 'text',
        value: selectedServiceType?.description ?? '',
        required: true, // AI Receptionist requires a service description in order to accurately respond to patient requestsz
      },
      shouldCollectPayment: {
        type: 'checkbox',
        value: (selectedServiceType?.prepaymentAmount ?? 0) > 0,
        hidden: !isPaymentsEnabled,
      },
      prePaymentAmount: {
        type: 'money',
        value: String(formatCentsToCurrency(selectedServiceType?.prepaymentAmount) ?? 0),
        hidden: !isPaymentsEnabled,
        validator: ({ value }) => {
          const amount = Number(value);
          if (amount === 0) return t('Amount must be greater than 0');
          if (amount % 1 !== 0) return t('Whole dollar amounts only');
          return '';
        },
      },
    },
  });

  const appointmentTypesQuery = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    pageConfig: { page: 1, limit: 500 },
    selectedLocationId: locationId ?? '',
    selectedLocationIds: [locationId ?? ''],
    childLocationId: locationId,
  });

  const sourceQuery = SchedulerV3.Helpers.useGetDataSourcesForLocation({ locationId });

  const createServiceMutation = SchedulerV3.Mutations.useCreateBookableAppointmentTypeMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Service type created successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to create service type'));
      },
    },
  });

  const updateServiceMutation = SchedulerV3.Mutations.useUpdateBookableAppointmentTypeMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Service type updated successfully'));
        closeModal();
      },
      onError: () => {
        alert.error(t('Failed to update service type'));
      },
    },
  });

  const handleSubmit = async () => {
    if (locationId) {
      SchedulerV3.trackPendoEvent(ServiceTypesSettingsTrackingIds.events.serviceSaveClicked, {
        isIntegrated: variant === 'integrated',
        locationId: locationId,
      });
    }

    const commonPayload = {
      ...(variant === 'core' && { color: values.color }),
      description: values.serviceDescription ?? '',
      displayName: values.serviceName ?? '',
      duration: Number(values.duration) || 0,
      locationId: locationId ?? '',
      prepaymentAmount: values.shouldCollectPayment ? Number(values.prePaymentAmount) * 100 : 0,
    };

    if (isEditMode) {
      updateServiceMutation.mutateAsync({
        ...commonPayload,
        bookableAppointmentTypeId: selectedServiceType.id ?? '',
      });
    } else {
      createServiceMutation.mutateAsync({
        ...commonPayload,
        ...(values.appointmentType && { appointmentTypeId: values.appointmentType }), // Not available while creating Service Type for core
        parentLocationId: parentLocationId ?? '',
        sourceId: values.source ?? '',
        sourceTenantId: sourceTenantId ?? '',
      });
    }
  };

  const closeModal = () => {
    onClose();
    reset();
  };

  return (
    <Tray {...modalProps} width='large' onClose={closeModal}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={closeModal}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <Heading level={2}>{title}</Heading>
          {showLocationChip && <Chip.Location maxWidth={'fit-content'}>{getScopeName(locationId)}</Chip.Location>}
        </div>
      </Tray.Header>
      <Tray.Body css={{ borderTop: `1px solid ${theme.colors.neutral20}`, padding: theme.spacing(2, 1) }}>
        <ServiceTypeForm
          variant={variant}
          appointmentTypes={appointmentTypesQuery.data?.appointmentTypes ?? []}
          sources={sourceQuery.data?.dataSources ?? []}
          procedureCodes={[]}
          getFieldProps={getFieldProps}
        />
      </Tray.Body>
      <Tray.Actions
        disabledPrimary={!isComplete || createServiceMutation.isLoading || updateServiceMutation.isLoading}
        primaryLabel={t('Save')}
        onPrimaryClick={handleSubmit}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={closeModal}
      />
    </Tray>
  );
};
